import { Contract } from 'web3-eth-contract';
import { AbiItem } from 'web3-utils';
import complianceRegistryABI from '../contractData/abis/complianceRegistry.json';
import kycVerificationABI from '../contractData/abis/identityVerification.json';
import assetImplimentationABI from '../contractData/abis/propertyAssetImplimentation.json';
import propertyFactoryABI from '../contractData/abis/propertyFactory.json';
import rentalPoolABI from '../contractData/abis/rentalAsset.json';
import resellPoolABI from '../contractData/abis/resellpool.json';
import tokenABI from '../contractData/abis/token.json';
import contractAddresses from '../contractData/contractAddress/addresses';
import { services } from '../services';

type ContractInstance<T extends AbiItem[]> = Contract<T>;

export const web3Actions = {
  getPropertyFactoryContract: async (): Promise<ContractInstance<typeof propertyFactoryABI>> => {
    try {
      const { propertyFactory } = contractAddresses();
      return await services.getContractInstance(propertyFactoryABI, propertyFactory);
    } catch (error) {
      console.error('Error initializing property factory contract:', error);
      throw error;
    }
  },

  getKycVerificationContract: async (): Promise<ContractInstance<typeof kycVerificationABI>> => {
    try {
      const { identityVerification } = contractAddresses();
      return await services.getContractInstance(kycVerificationABI, identityVerification);
    } catch (error) {
      console.error('Error initializing KYC verification contract:', error);
      throw error;
    }
  },

  getComplianceRegistryContract: async (): Promise<
    ContractInstance<typeof complianceRegistryABI>
  > => {
    try {
      const { complianceRegistry } = contractAddresses();
      return await services.getContractInstance(complianceRegistryABI, complianceRegistry);
    } catch (error) {
      console.error('Error initializing compliance registry contract:', error);
      throw error;
    }
  },

  getRentalPoolContract: async (): Promise<ContractInstance<typeof rentalPoolABI>> => {
    try {
      const { rentalPool } = contractAddresses();
      return await services.getContractInstance(rentalPoolABI, rentalPool);
    } catch (error) {
      console.error('Error initializing rental pool contract:', error);
      throw error;
    }
  },

  getResellPoolContract: async (): Promise<ContractInstance<typeof resellPoolABI>> => {
    try {
      const addresses = contractAddresses();
      const { resellPool } = addresses;

      if (!resellPool) {
        throw new Error('ResellPool contract address is undefined');
      }

      return await services.getContractInstance(resellPoolABI, resellPool);
    } catch (error) {
      console.error('Error initializing resell pool contract:', error);
      throw error;
    }
  },

  getPropertyFactoryContractForView: async (): Promise<
    ContractInstance<typeof propertyFactoryABI> | undefined
  > => {
    try {
      const { propertyFactory } = contractAddresses();
      const contract = await services.getContractInstance(propertyFactoryABI, propertyFactory);
      if (!contract) {
        throw new Error('Failed to get property factory contract for view');
      }
      return contract;
    } catch (error) {
      console.error('Error initializing property factory contract for view:', error);
      throw error;
    }
  },

  /**
   * Get property asset contract instance
   *
   * Note: Payment token addresses are now dynamically configured via environment variables
   * in the tokenAddresses configuration system. See src/contractData/tokenAddresses/index.ts
   * for token address management.
   */
  getPropertyAssetContract: async (
    contractAddress: string
  ): Promise<ContractInstance<typeof assetImplimentationABI> | undefined> => {
    try {
      const contract = await services.getContractInstance(assetImplimentationABI, contractAddress);
      if (!contract) {
        throw new Error('Failed to get property asset contract');
      }
      return contract;
    } catch (error) {
      console.error('Error initializing property asset contract:', error);
      throw error;
    }
  },

  getTokenContract: async (): Promise<ContractInstance<typeof tokenABI>> => {
    try {
      const { token } = contractAddresses();
      return await services.getContractInstance(tokenABI, token);
    } catch (error) {
      console.error('Error initializing token contract:', error);
      throw error;
    }
  },
};
